<script setup lang="ts">
type EnumData = App.Base.Data.EnumData;
type StudentUserData = App.Auth.Data.StudentUserData;

import type { Selectable } from '@/components/selectBox/selectBox';

import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';

import { buildSelectableEnum, buildSelectableSchool } from '@/utils/buildSelectable';

import Alert from '@/components/alert/Alert.vue';
import Button from '@/components/button/Button.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import EmptyCard from '@/components/emptyCard/EmptyCard.vue';
import Fieldset from '@/components/fieldset/Fieldset.vue';
import FormInlineFields from '@/components/formInlineFields/FormInlineFields.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';
import FormMultiComboboxInput from '@/components/formComboboxInput/FormMultiComboboxInput.vue';
import FormRows from '@/components/formRows/FormRows.vue';
import FormSchoolInput from '@/components/formComboboxInput/FormSchoolInput.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';
import IconButton from '@/components/button/IconButton.vue';

import Check from '@/icons/line/check.svg';
import InfoCircle from '@/icons/line/info-circle.svg';
import PlusCircle from '@/icons/line/plus-circle.svg';
import Trash from '@/icons/line/trash.svg';

const props = defineProps<{
  user: StudentUserData;
  postSecondaryPlans: EnumData[];
  majors: EnumData[];
  grades: EnumData[];
}>();

const { show, close, closeLocally } = useDialog();
const [isCancelDialogOpen, toggleCancelDialog] = useToggle(false);
const [isContactAdviserDialogOpen, toggleContactAdviserDialog] = useToggle(false);

const isDialogOpen = computed(
  () => !isCancelDialogOpen.value && !isContactAdviserDialogOpen.value && show.value
);

const form = useForm({
  url: route('student.profile.enrolment.update'),
  method: 'PUT',
  fields: {
    school_id: buildSelectableSchool(props.user.school),
    grade: props.user.grade as Selectable<number> | null,
    class_of: props.user.class_of,
    gpa: props.user.gpa,
    free_lunch: props.user.free_lunch
      ? ({
          label: 'Yes',
          value: true
        } as Selectable<boolean>)
      : ({
          label: 'No or not sure',
          value: false
        } as Selectable<boolean>),
    post_secondary_plans: (props.user.post_secondary_plans || []).map((post_secondary_plan) =>
      buildSelectableEnum(post_secondary_plan.post_secondary_plan)
    ) as Selectable<string>[],
    majors: props.user.majors
      ? props.user.majors.map((major) => major.major as Selectable<number>)
      : []
  },
  transform: (fields) => ({
    free_lunch: fields.free_lunch?.value,
    post_secondary_plans: (fields.post_secondary_plans || []).map(
      (post_secondary_plan) => post_secondary_plan?.value
    ),
    majors: (fields.majors || []).map((major) => major?.value)
  }),
  hooks: {
    success() {
      close();
    }
  }
});

function handleFormSubmit() {
  form.submit();
}

function handleAddMajor() {
  if (!form.fields.majors) {
    form.fields.majors = [];
  }
  form.fields.majors.push({
    ...props.majors[0]
  });
}

function handleRemoveMajor(index: number) {
  if (form.fields.majors) {
    form.fields.majors.splice(index, 1);
  }
}

function handleUpdateMajors(index: number, value: Selectable<number> | null) {
  if (form.fields.majors && value !== null) {
    form.fields.majors[index] = value;
  }
}

function handleToggleCancelDialog() {
  if (form.isDirty) {
    toggleCancelDialog();
    return;
  }
  closeLocally();
}

function handleCancelDialogConfirm() {
  toggleCancelDialog();
  closeLocally();
}

function handleNavigateToAdviser() {
  router.navigate({ url: route('student.adviser.index') });
}

function handleContactAdviserClick() {
  if (form.isDirty) {
    toggleContactAdviserDialog();
    return;
  }
  handleNavigateToAdviser();
}

function handleContactAdviserDialogConfirm() {
  form.submitWith({
    hooks: {
      success() {
        close();
        // TODO: Update when route is available
        handleNavigateToAdviser();
      },
      after() {
        toggleContactAdviserDialog();
      }
    }
  });
}

function handleContactAdviserDialogCancel() {
  toggleContactAdviserDialog();
  handleNavigateToAdviser();
}
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/student-parent.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <Dialog
    title="Enrolment and Academics"
    :cancelButtonDisabled="!form.isDirty"
    :confirmButtonIcon="Check"
    :isLoading="form.processing"
    :isOpen="isDialogOpen"
    cancelButtonLabel="Reset"
    confirmButtonLabel="Save changes"
    @onCancel="form.reset"
    @onClose="handleToggleCancelDialog"
    @onConfirm="handleFormSubmit"
  >
    <template #form>
      <div class="-mx-5 -mb-8 -mt-6 flex flex-col divide-y divide-slate-200">
        <FormRows class="px-5 pb-8 pt-6">
          <FormSchoolInput
            :modelValue="form.fields.school_id"
            isDisabled
            label="School"
            name="school_id"
            placeholder="Select school"
          />
          <FormInlineFields>
            <FormListboxInput
              :options="grades"
              :modelValue="form.fields.grade"
              isDisabled
              label="Grade"
            />
            <FormTextInput
              :modelValue="form.fields.class_of"
              isDisabled
              label="Class of"
              name="class_of"
              placeholder="e.g. 2024"
              type="number"
            />
          </FormInlineFields>
          <Alert
            :icon="InfoCircle"
            color="secondary"
            title="Is your school or grade incorrect?"
            variant="soft"
            hideClose
          >
            <p>
              <button
                type="button"
                class="font-inherit leading-inherit cursor-pointer border-none bg-transparent p-0 text-inherit underline hover:no-underline"
                @click="handleContactAdviserClick"
              >
                Contact your adviser
              </button>
              to make a correction.
            </p>
          </Alert>
          <FormTextInput
            :modelValue="form.fields.gpa"
            isDisabled
            label="GPA"
            name="gpa"
            step="any"
            type="number"
          />
          <FormListboxInput
            label="Do you qualify for a free/reduced-price meals at school?"
            :error="form.errors.free_lunch as string"
            :options="[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No or not sure',
                value: false
              }
            ]"
            :modelValue="form.fields.free_lunch"
            class="grow"
            @update:modelValue="(value) => (form.fields.free_lunch = value as Selectable<boolean>)"
          />
        </FormRows>
        <FormRows class="px-5 pb-8 pt-6">
          <Fieldset title="Post-secondary">
            <FormMultiComboboxInput
              :error="form.errors.post_secondary_plans?.[0] as string"
              :modelValue="form.fields.post_secondary_plans as Selectable<string>[]"
              :options="postSecondaryPlans"
              label="Post-secondary plans"
              @update:modelValue="(value) => (form.fields.post_secondary_plans = value)"
            />
          </Fieldset>
          <Fieldset title="Post-secondary Majors">
            <template v-if="form.fields.majors && form.fields.majors.length > 0">
              <div class="flex flex-col gap-2.5">
                <div class="flex flex-col gap-5">
                  <div
                    v-for="(major, index) in form.fields.majors"
                    :key="major.value"
                    class="flex items-end gap-2"
                  >
                    <FormListboxInput
                      :label="`Major ${index + 1}`"
                      :error="form.errors?.majors?.[index] as string"
                      :options="majors"
                      :modelValue="major as Selectable<number>"
                      class="grow"
                      @update:modelValue="(value) => handleUpdateMajors(index, value)"
                    />
                    <IconButton
                      :icon="Trash"
                      ariaLabel="Remove major"
                      color="danger"
                      variant="invisible"
                      @click="handleRemoveMajor(index)"
                    />
                  </div>
                </div>
                <div>
                  <Button
                    :color="ButtonColor.slate"
                    :iconLeft="PlusCircle"
                    :size="ButtonSize.xs"
                    :variant="ButtonVariant.invisible"
                    @click="handleAddMajor"
                    >Add a major</Button
                  >
                </div>
              </div>
            </template>
            <EmptyCard
              v-else
              title="No major added"
              description="Add a major by clicking the button below"
              actionLabel="New major"
              isDashed
              isRounded
              variant="invisible"
              size="md"
              @actionClick="handleAddMajor"
            />
          </Fieldset>
        </FormRows>
      </div>
    </template>
  </Dialog>
  <DangerDialog
    title="Discard Changes?"
    message="Your changes will not be saved."
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Discard changes"
    isStacked
    @onCancel="() => toggleCancelDialog()"
    @onClose="() => toggleCancelDialog()"
    @onConfirm="handleCancelDialogConfirm"
  />
  <Dialog
    title="Save Changes First?"
    message="You have unsaved changes. Would you like to save them before contacting your adviser?"
    :isOpen="isContactAdviserDialogOpen"
    :isLoading="form.processing"
    :confirmButtonIcon="Check"
    confirmButtonLabel="Save and continue"
    cancelButtonLabel="Continue without saving"
    isStacked
    @onCancel="() => handleContactAdviserDialogCancel()"
    @onClose="() => toggleContactAdviserDialog()"
    @onConfirm="handleContactAdviserDialogConfirm"
  />
</template>
